import { Alert, Box, Button, Chip, Stack, Tab, Tabs, Typography } from "@mui/material"
import { FC, useMemo, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router"
import { fmtDurationMs } from "shared/reservationSyncData"
import {
  ReservationSyncDto,
  useGetReservationSyncsQuery,
} from "src/features/things/reservationSyncApi"
import SaLayout from "./SaLayout"
import { ConfirmationRecord } from "./reservationSync/ConfirmationRecord"
import { DriftPanel } from "./reservationSync/DriftPanel"
import { InconclusiveNotice } from "./reservationSync/InconclusiveNotice"
import { ObjectsPanel } from "./reservationSync/ObjectsPanel"
import { OverviewPanel } from "./reservationSync/OverviewPanel"
import {
  fmtBytes,
  fmtCount,
  fmtNum,
  ReadStateChip,
  VerdictChip,
  VersionBadge,
} from "./reservationSync/ReservationSyncChrome"
import { SourceCards } from "./reservationSync/SourceCards"
import { TenantContextRow } from "./reservationSync/TenantContextRow"
import { WriteLedgerPanel } from "./reservationSync/WriteLedgerPanel"

const TABS = ["overview", "drift", "sync", "revert", "objects"] as const
type TabName = (typeof TABS)[number]

const LADDER: { key: string; label: string }[] = [
  { key: "DRIFT_ASSESSED", label: "drift assessed" },
  { key: "SYNC_APPLIED", label: "sync applied" },
  { key: "SYNC_REVERTED", label: "sync reverted" },
]

export const SaReservationSyncPage: FC = () => {
  const { runId } = useParams<{ runId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const tenantId = searchParams.get("tenantId") ?? undefined
  const [tab, setTab] = useState<TabName>("overview")

  // The list query is cached, so arriving from the list costs nothing; arriving by
  // URL fetches once. A dedicated by-id endpoint would be a second access pattern
  // for the same partition read.
  const { data, isLoading } = useGetReservationSyncsQuery({ limit: 2000 })
  const run = useMemo(
    () => (data?.things ?? []).find((r) => r.runId === runId),
    [data, runId],
  )

  /**
   * Other lockers in this tenant, latest run each.
   *
   * Context only, and the dedupe matters: a tenant swept weekly has one row per
   * locker per run, and listing them all would make the sibling panel a history
   * rather than a "what else is here".
   */
  const siblings = useMemo(() => {
    if (!run) return []
    const latestByLocker = new Map<string, ReservationSyncDto>()
    for (const candidate of data?.things ?? []) {
      if (candidate.tenantId !== run.tenantId) continue
      if (candidate.lockerId === run.lockerId) continue
      const held = latestByLocker.get(candidate.lockerId)
      if (!held || held.startedAt < candidate.startedAt)
        latestByLocker.set(candidate.lockerId, candidate)
    }
    return [...latestByLocker.values()]
  }, [data, run])

  if (isLoading)
    return (
      <SaLayout selectedMenuItemLabelKey="reservationSyncs">
        <Box sx={{ p: 5 }}>
          <Typography variant="body2">loading the run…</Typography>
        </Box>
      </SaLayout>
    )

  if (!run)
    return (
      <SaLayout selectedMenuItemLabelKey="reservationSyncs">
        <Box sx={{ p: 5 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Run not found</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            No stored run with id <code>{runId}</code>
            {tenantId ? ` in tenant ${tenantId}` : ""}. Its record may have been created
            in another environment, or the run id may be mistyped.
          </Typography>
          <Button size="small" onClick={() => navigate("/sa/reservation-syncs")}>
            All sync runs
          </Button>
        </Box>
      </SaLayout>
    )

  const totalBytes = run.objects.reduce((a, o) => a + o.bytes, 0)
  const inconclusive = run.verdict === "INCONCLUSIVE"

  return (
    <SaLayout selectedMenuItemLabelKey="reservationSyncs">
      <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 3, md: 5 }, py: { xs: 4, md: 5 } }}>
        <Button size="small" onClick={() => navigate("/sa/reservation-syncs")} sx={{ mb: 1 }}>
          ← All sync runs
        </Button>

        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderLeft: 4,
            borderLeftColor: inconclusive
              ? "error.main"
              : run.sstApiVersion === "v2"
                ? "secondary.main"
                : "primary.main",
            borderRadius: 1,
            p: 2.5,
            mb: 3,
          }}
        >
          <TenantContextRow run={run} siblingLockerCount={siblings.length + 1} />

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <VersionBadge version={run.sstApiVersion} size="medium" />
            <Typography sx={{ fontSize: "1.15rem", fontWeight: 600 }}>
              {run.lockerName ?? run.sstLockerId}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontFamily: "monospace", color: "text.secondary" }}
            >
              {run.sstLockerId}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <VerdictChip verdict={run.verdict} size="medium" />
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }} flexWrap="wrap">
            {LADDER.map((step, i) => {
              const reached =
                run.status === step.key ||
                LADDER.findIndex((s) => s.key === run.status) > i
              return (
                <Chip
                  key={step.key}
                  size="small"
                  label={step.label}
                  variant={reached ? "filled" : "outlined"}
                  color={
                    run.status === "SYNC_FAILED" && i === 1
                      ? "error"
                      : reached
                        ? "success"
                        : "default"
                  }
                />
              )
            })}
            {/* No `RunStatusChip` here: the ladder above already IS the status, and
                showing both rendered "drift assessed" twice in the same row. */}
            {/* Kept beside the four source cards, not instead of them: it is the
                WORST of the four, and a reader needs to know which one it came from. */}
            <ReadStateChip readState={run.readState} />
          </Stack>

          <Box sx={{ mt: 2.5 }}>
            <SourceCards run={run} />
          </Box>

          {run.verdict === "QUALIFIED" && (
            <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
                <strong>Qualified, not actionable.</strong> A source reports no
                total, so its read is <code>UNVERIFIED</code> by construction — on
                SST v2 that is permanent, since no v2 endpoint reports a per-locker
                count. The numbers below are shown because that walk ended on a
                short page with its cap untouched, which is positive evidence of
                exhaustion. A cap hit or a failed read downgrades the run to{" "}
                <code>INCONCLUSIVE</code> and no count is rendered at all.
              </Typography>
            </Alert>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 2,
              mt: 2.5,
              pt: 2,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            {(
              [
                ["Run id", run.runId],
                ["Operator profile", run.operatorProfile],
                // An em dash on an inconclusive run, never a zero.
                ["Drift", fmtCount(run.driftTotal)],
                ["Fields compared", run.fieldsCompared.join(", ")],
                ["Stored", `${fmtBytes(totalBytes)} · ${run.objects.length} object(s)`],
                ["Started", run.startedAt.replace("T", " ").slice(0, 19)],
                // This locker's OWN time, not finishedAt - startedAt: startedAt is
                // the sweep's start instant, shared across every run it published.
                ["Assessment took", fmtDurationMs(run.sweepDurationMs)],
              ] as const
            ).map(([k, v]) => (
              <Box key={k}>
                <Typography
                  variant="caption"
                  sx={{ color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em" }}
                >
                  {k}
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", fontFamily: "monospace", wordBreak: "break-all" }}>
                  {v}
                </Typography>
              </Box>
            ))}
          </Box>

          {!!run.errors?.length && (
            <Typography variant="caption" sx={{ display: "block", mt: 1, color: "error.main" }}>
              {run.errors.join(" · ")}
            </Typography>
          )}
        </Box>

        {inconclusive && <InconclusiveNotice run={run} />}

        <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
          {TABS.map((name) => (
            <Tab key={name} value={name} label={name} sx={{ textTransform: "capitalize" }} />
          ))}
        </Tabs>

        {tab === "overview" && <OverviewPanel run={run} siblings={siblings} />}
        {tab === "drift" && <DriftPanel run={run} />}
        {tab === "sync" && (
          <>
            <ConfirmationRecord run={run} />
            <WriteLedgerPanel run={run} kind="sync" />
          </>
        )}
        {tab === "revert" && <WriteLedgerPanel run={run} kind="revert" />}
        {tab === "objects" && <ObjectsPanel run={run} />}

        {/* Deliberately unqualified: partner rows / our rows are still on the
            record, and the four cards above replace them as the primary read. */}
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 3, color: "text.disabled" }}
        >
          Partner rows {fmtNum(run.sstFetched)}
          {run.sstTotalReported === null
            ? " (no total reported)"
            : ` of ${fmtNum(run.sstTotalReported)}`}{" "}
          · our rows {fmtNum(run.sl2Fetched)}
        </Typography>
      </Box>
    </SaLayout>
  )
}

export default SaReservationSyncPage
